import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, HeartHandshake, MessageCircleQuestion, Cross, Church, Flame, Sprout, CloudSun, Sunrise, BookOpen } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { RESOURCES, RESOURCE_LIST } from "@/data/resources";
import { RESOURCE_ICONS } from "@/pages/Resources";
import { track } from "@/lib/analytics";

export default function ResourcePage() {
  const { slug } = useParams();
  const r = RESOURCES[slug];

  useEffect(() => {
    if (r) track("resource_view", { slug });
  }, [slug, r]);

  if (!r) return <Navigate to="/resources" replace />;

  const others = RESOURCE_LIST.filter((o) => o.slug !== slug).slice(0, 3);
  const HeroIcon = RESOURCE_ICONS[r.icon] || BookOpen;

  return (
    <>
      <SEO title={r.title} description={r.short + " A short, biblical two-minute read from Victory Chapel Christian Church in Clay County, Florida."} path={`/resources/${slug}`} />

      <section className="grain relative overflow-hidden bg-navy pb-14 pt-14 text-white sm:pb-16 sm:pt-20" data-testid="resource-hero">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <nav className="text-xs uppercase tracking-[0.2em] text-white/50" aria-label="Breadcrumb">
              <Link to="/" className="transition-colors hover:text-white">Home</Link>
              <span className="mx-2" aria-hidden="true">/</span>
              <Link to="/resources" className="transition-colors hover:text-white">Free Resources</Link>
              <span className="mx-2" aria-hidden="true">/</span>
              <span className="text-white/80">{r.title}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 flex items-center gap-2.5 font-display text-xs font-700 uppercase tracking-[0.25em] text-rose-300">
              <HeroIcon size={16} aria-hidden="true" /> Free Resource - 2 min read
            </p>
          </Reveal>
          <LineReveal
            className="mt-4 font-display text-4xl font-900 leading-[1.06] tracking-tighter sm:text-5xl lg:text-6xl"
            lines={[r.title.toUpperCase()]}
            delay={0.15}
          />
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-2xl font-serif text-lg italic leading-relaxed text-white/85 sm:text-xl">{r.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="resource-content">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Stagger className="space-y-10">
            {r.points.map((p, i) => (
              <StaggerItem key={i}>
                <div data-testid={`resource-point-${i + 1}`}>
                  <p className="text-base leading-relaxed text-slate-700 sm:text-lg">{p.text}</p>
                  <blockquote className="mt-4 border-l-2 border-victory pl-5">
                    <p className="font-serif text-lg italic leading-snug text-navy sm:text-xl">"{p.verse}"</p>
                    <cite className="mt-2 block font-display text-xs font-700 uppercase not-italic tracking-[0.25em] text-victory">{p.ref}</cite>
                  </blockquote>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          {r.prayer && (
            <Reveal delay={0.1}>
              <div className="mt-12 rounded-3xl border border-navy/10 bg-cream p-8 sm:p-10" data-testid="resource-prayer">
                <p className="font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">{r.prayer.title}</p>
                <p className="mt-4 font-serif text-xl italic leading-relaxed text-navy sm:text-2xl">"{r.prayer.text}"</p>
                {r.prayer.note && <p className="mt-4 text-sm leading-relaxed text-slate-600">{r.prayer.note}</p>}
              </div>
            </Reveal>
          )}

          {r.crisis && (
            <Reveal delay={0.1}>
              <div className="mt-12 rounded-2xl border border-victory/30 bg-victory/5 p-6" data-testid="resource-crisis-note" role="note">
                <p className="text-sm leading-relaxed text-navy">{r.crisis}</p>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <p className="mt-12 text-center font-serif text-xl italic leading-relaxed text-slate-600 sm:text-2xl" data-testid="resource-closing">
              {r.closing}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-16" data-testid="resource-cta">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="grain relative overflow-hidden rounded-3xl bg-navy p-8 text-center text-white shadow-lift sm:p-12">
              <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <MessageCircleQuestion size={30} className="mx-auto text-rose-300" aria-hidden="true" />
                <h2 className="mt-4 text-balance font-display text-2xl font-800 tracking-tight sm:text-3xl">
                  QUESTIONS? WE'D LOVE TO TALK.
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/75">
                  Whether you have questions, want to know more, or simply need prayer - reach out.
                  A real person from Victory Chapel will answer you.
                </p>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                  <Link to="/contact" data-testid="resource-contact-cta"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-8 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                    CONTACT US <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                  <Link to="/pray" data-testid="resource-pray-cta"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-3.5 font-display text-sm font-700 text-white transition-colors hover:bg-white/10">
                    REQUEST PRAYER
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-10 text-center font-display text-xs font-700 uppercase tracking-[0.3em] text-slate-400">Keep Reading</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2.5" data-testid="resource-more">
              {others.map((o) => (
                <Link key={o.slug} to={`/resources/${o.slug}`} data-testid={`resource-more-${o.slug}`}
                      className="rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
                  {o.title}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
